import React, { useMemo, useState } from "react";
import styled from "styled-components";

import { TAG_SELECTION_OPTIONS } from "../../constants/form";

import { useSelector } from "services/redux/rootReducer";

import { Tag } from "features/shared/types/tag";
import { InfoAdapterType } from "features/shared/types/info";
import { verifyForm } from "features/tagEdit/functions/form";
import ImageInput from "features/shared/components/form/components/imageInput";
import BasicInput from "features/shared/components/form/components/basicInput";
import SubmitButton from "features/shared/components/form/components/submitButton";
import useSubmit from "features/shared/components/form/hooks/useSubmit";
import SelectInput from "features/shared/components/form/components/selectInput";
import TextEditorInput from "features/shared/components/form/components/textEditorInput";
import AutoCompleteSelect from "features/shared/components/form/components/autoCompleteSelect";
import LabelWrapper from "features/shared/components/form/components/labelWrapper";
import usePureState from "features/shared/hooks/usePureState";
import AutoCompleteArticleId from "./autoCompleteArticleId";

interface Props {
    onSubmitCallback: (fields: Tag) => void;
    submitBtnText: string;
    originalFields?: Tag;
    readOnlyMode?: boolean;
    initialForm: Tag;
}

const TagForm: React.FC<Props> = ({
    onSubmitCallback,
    submitBtnText,
    originalFields,
    readOnlyMode = false,
    initialForm,
}) => {
    const [fields, setFields] = usePureState<Tag>(
        originalFields || initialForm
    );
    const [imageFile, setImageFile] = useState<File | null>();

    const tags = useSelector((state) => state.tags);

    const [handleSubmit] = useSubmit({
        fields,
        readOnlyMode,
        handleFormVerification: async (fields) => {
            return await verifyForm({
                fields,
                tags,
                isUpdating: originalFields !== undefined,
                originalFields,
            });
        },
        imageUploadOptions:
            fields.value.type === InfoAdapterType.DefaultInfo
                ? {
                      originalImageUrl:
                          originalFields?.value.type ===
                          InfoAdapterType.DefaultInfo
                              ? originalFields.value.image
                              : "",
                      fieldsImageUrl: fields.value.image,
                      imageFile,
                      submitAccessor: "value:image",
                  }
                : undefined,
        onSubmitCallback,
    });

    const initialSuggestedQueries = useMemo(() => {
        switch (fields.value.type) {
            case InfoAdapterType.DefaultInfo:
                return ["category:", "supplier:", "process:", "application:"];
            case InfoAdapterType.StringInfo:
                return ["custom:"];
            default:
                return [];
        }
    }, [fields.value.type]);

    const handleRawChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        handleChange(e.target.name, e.target.value);
    };

    const handleChange = (name: string, value: string) => {
        if (readOnlyMode) return;

        let clonedFields = {
            ...fields,
        };

        if (name === "key") {
            clonedFields.key = value;
        } else {
            switch (clonedFields.value.type) {
                case InfoAdapterType.DefaultInfo: {
                    switch (name) {
                        case "value:name:en":
                            clonedFields.value.name = {
                                en: value,
                                th: clonedFields.value.name?.th || "",
                            };
                            break;
                        case "value:name:th":
                            clonedFields.value.name = {
                                en: clonedFields.value.name?.en || "",
                                th: value,
                            };
                            break;
                        case "value:description:en":
                            clonedFields.value.description = {
                                en: value,
                                th: clonedFields.value.description?.th || "",
                            };
                            break;
                        case "value:description:th":
                            clonedFields.value.description = {
                                en: clonedFields.value.description?.en || "",
                                th: value,
                            };
                            break;
                        case "value:rank":
                            const rank = parseInt(value);
                            if (!rank) delete clonedFields.value.rank;
                            else clonedFields.value.rank = rank;
                            break;
                        case "value:seo":
                            clonedFields.value.seo = value;
                            break;
                        case "value:linkedArticleId":
                            clonedFields.value.linkedArticleId = value;
                            break;
                    }
                    break;
                }
                case InfoAdapterType.StringInfo: {
                    switch (name) {
                        case "value:value":
                            clonedFields.value.value = value;
                            break;
                    }
                }
            }
        }

        setFields(clonedFields);
    };

    const handleImageData = (file: File | null) => {
        setImageFile(file);
    };

    // Handles the 'type' field
    const handleSelect = (type: InfoAdapterType) => {
        if (type === fields.value.type) return;

        switch (type) {
            case InfoAdapterType.DefaultInfo: {
                return setFields({
                    key: "",
                    value: {
                        type: InfoAdapterType.DefaultInfo,
                    },
                });
            }
            case InfoAdapterType.StringInfo: {
                return setFields({
                    key: "custom:",
                    value: {
                        type: InfoAdapterType.StringInfo,
                        value: "",
                    },
                });
            }
        }
    };

    return (
        <StyledForm onSubmit={handleSubmit} autoComplete="off">
            {fields?.value.type === InfoAdapterType.DefaultInfo ? (
                <LabelWrapper label="Image">
                    <ImageInput
                        initialImageUrl={fields.value.image}
                        onChangeHandler={handleImageData}
                        readOnlyMode={readOnlyMode}
                    />
                </LabelWrapper>
            ) : null}
            <LabelWrapper
                label={(readOnlyMode ? "Selected " : "Choose ") + "Tag Type"}
            >
                <SelectInput
                    value={fields.value.type}
                    readOnlyMode={readOnlyMode}
                    selectionOptions={TAG_SELECTION_OPTIONS}
                    onSelectHandler={handleSelect}
                />
            </LabelWrapper>
            <LabelWrapper
                label={
                    (readOnlyMode ? "Current " : "Set ") +
                    "Key (Cannot be updated)"
                }
            >
                <AutoCompleteSelect
                    name="key"
                    queriedStrings={tags.map((tag) => tag.key)}
                    initialSuggestions={initialSuggestedQueries}
                    value={fields?.key}
                    onChangeHandler={handleChange}
                    readOnly={originalFields !== undefined}
                />
            </LabelWrapper>
            {fields?.value.type === InfoAdapterType.StringInfo ? (
                <LabelWrapper
                    label={(readOnlyMode ? "Current " : "Set ") + "Text"}
                >
                    <BasicInput
                        name="value:value"
                        value={fields.value.value}
                        onChangeHandler={handleRawChange}
                    />
                </LabelWrapper>
            ) : null}
            {fields?.value.type === InfoAdapterType.DefaultInfo ? (
                <>
                    <LabelWrapper label="NAME (EN)">
                        <BasicInput
                            name="value:name:en"
                            value={fields.value.name?.en}
                            onChangeHandler={handleRawChange}
                        />
                    </LabelWrapper>
                    <LabelWrapper label="NAME (TH)">
                        <BasicInput
                            name="value:name:th"
                            value={fields.value.name?.th}
                            onChangeHandler={handleRawChange}
                        />
                    </LabelWrapper>
                    <LabelWrapper label="Linked Article ID">
                        <AutoCompleteArticleId
                            onChangeHandler={handleChange}
                            value={fields.value.linkedArticleId}
                            name="value:linkedArticleId"
                        />
                    </LabelWrapper>
                    <LabelWrapper label="Description (EN)">
                        <TextEditorInput
                            name="value:description:en"
                            value={fields.value.description?.en}
                            readOnlyMode={readOnlyMode}
                            onEditorChangeHandler={handleChange}
                        />
                    </LabelWrapper>
                    <LabelWrapper label="Description (TH)">
                        <TextEditorInput
                            name="value:description:th"
                            value={fields.value.description?.th}
                            readOnlyMode={readOnlyMode}
                            onEditorChangeHandler={handleChange}
                        />
                    </LabelWrapper>
                    <LabelWrapper label="Rank">
                        <BasicInput
                            name="value:rank"
                            value={fields.value.rank?.toString()}
                            onChangeHandler={handleRawChange}
                        />
                    </LabelWrapper>
                    <LabelWrapper label="SEO">
                        <BasicInput
                            name="value:seo"
                            value={fields.value.seo}
                            onChangeHandler={handleRawChange}
                        />
                    </LabelWrapper>
                </>
            ) : null}
            {!readOnlyMode && <SubmitButton text={submitBtnText} />}
        </StyledForm>
    );
};

const StyledForm = styled.form`
    overflow: auto;
    padding: 5%;
    color: ${(props) => props.theme.colors.neutral[800]};
`;

export default TagForm;
